import { useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, ArrowRight, ArrowUpRight, Building2, Mail, Pencil, Phone, Pin, Send, Trash2, UserCheck2 } from "lucide-react";
import { leadsApi, type LeadStatus, type DisqualificationReason, type LeadConversionTarget } from "@/api/leads";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { leadKeys } from "@/hooks/leads/leadKeys";
import {
  Badge,
  Button,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
  Textarea,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { AssigneePicker } from "@/components/broker-crm/AssigneePicker";
import { LeadGeneratorPicker } from "@/components/broker-crm/LeadGeneratorPicker";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useDateFormatter } from "@/utils/datetime";
import { LeadOverviewActivityPreview } from "./components/LeadOverviewActivityPreview";
import { BrokerageAffiliationsPanel } from "@/components/brokerages/BrokerageAffiliationsPanel";
import { SendApplicationLinkModal } from "./SendApplicationLinkModal";
import { DisqualifyLeadModal } from "./DisqualifyLeadModal";
import { PreleadDetailDrawer } from "@/pages/leadGeneration/PreleadDetailDrawer";

const STATUS_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  New: ["Contacted", "Disqualified"],
  Contacted: ["Interested", "NotInterested", "Unresponsive", "Disqualified"],
  Interested: ["NotInterested", "Disqualified"],
  NotInterested: ["Contacted"],
  Unresponsive: ["Contacted"],
  Disqualified: ["Contacted"],
  HandedOff: [],
};

// A Disqualified transition carries a structured reason code (+ optional
// notes); every other transition is target-only.
type ChangeStatusVars = {
  target: LeadStatus;
  reason?: DisqualificationReason;
  notes?: string;
};

// Layout Standard §3 — canonical tab order for entities without
// module-specific tabs. Lead is in this group in v1.0.
type TabKey =
  | "overview"
  | "brokerages"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabKey[] = [
  "overview",
  "brokerages",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "brokerages", label: "Brokerages", icon: Building2 },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabKey(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function LeadDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const { hasPermission, hasRoleCode } = useAuth();
  const canConvert = hasPermission("Leads.Convert");
  const canUpdate = hasPermission("Leads.Update");
  const canDelete = hasPermission("Leads.Delete");
  const { date } = useDateFormatter();

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabKey = isTabKey(tabParam) ? tabParam : "overview";

  const setTab = (next: TabKey) => {
    const updated = new URLSearchParams(params);
    if (next === "overview") updated.delete("tab");
    else updated.set("tab", next);
    setParams(updated, { replace: true });
  };

  const detail = useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: () => leadsApi.get(id),
    enabled: !!id,
  });
  const lead = detail.data;

  useBreadcrumbs(
    lead
      ? [
          { label: "Leads", href: "/leads" },
          { label: `${lead.firstName} ${lead.lastName}`.trim() || "Lead" },
        ]
      : null,
  );

  const assignableUsers = useAssignableUsers();
  const resolveUserName = (userId?: string | null): string | null => {
    if (!userId) return null;
    return assignableUsers.data?.find((u) => u.id === userId)?.displayName
      ?? userId.slice(0, 8);
  };

  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [disqualifyOpen, setDisqualifyOpen] = useState(false);
  const [sourcePreleadOpen, setSourcePreleadOpen] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: leadKeys.all });

  // One mutation for every status transition. Disqualified carries a required
  // reason code (enum) + optional notes; the rest are target-only. onError
  // surfaces the failure instead of silently no-opping (the old disqualify
  // bug: a free-text reason 400'd on the server and the rejected promise was
  // swallowed). onSuccess refetches and closes the disqualify modal.
  const changeStatus = useMutation({
    mutationFn: ({ target, reason, notes: disqNotes }: ChangeStatusVars) =>
      reason
        ? leadsApi.changeStatus(id, target, reason, disqNotes)
        : leadsApi.changeStatus(id, target),
    onSuccess: () => {
      invalidate();
      setDisqualifyOpen(false);
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Status change failed", tone: "error", message: e.message });
    },
  });

  const setLeadGenerator = useMutation({
    mutationFn: (sourceGeneratorId: string | null) =>
      leadsApi.setLeadGenerator(id, sourceGeneratorId),
    onSuccess: () => { invalidate(); },
    onError: (e: Error) =>
      void dialogs.alert({ title: "Attribution failed", tone: "error", message: e.message }),
  });

  const assign = useMutation({
    mutationFn: (userId: string) => leadsApi.assign(id, userId),
    onSuccess: invalidate,
    onError: (e: Error) => {
      void dialogs.alert({ title: "Reassign failed", tone: "error", message: e.message });
    },
  });

  // Leads Archive v1 — reversible soft-archive of a disqualified lead. Rides
  // existing lead-edit rights (Leads.Update). invalidate() drops it out of /
  // back into the list + stat counters without a manual refresh.
  const archive = useMutation({
    mutationFn: () => leadsApi.archive(id),
    onSuccess: invalidate,
    onError: (e: Error) => {
      void dialogs.alert({ title: "Archive failed", tone: "error", message: e.message });
    },
  });

  const unarchive = useMutation({
    mutationFn: () => leadsApi.unarchive(id),
    onSuccess: invalidate,
    onError: (e: Error) => {
      void dialogs.alert({ title: "Unarchive failed", tone: "error", message: e.message });
    },
  });

  // Shadow-delete — soft-delete the lead (IsDeleted=true server-side). Unlike
  // Archive this is not status-gated and drops the lead out of every query via
  // the global soft-delete filter, so on success we leave the now-gone detail
  // page and invalidate the list + counters.
  const deleteLead = useMutation({
    mutationFn: () => leadsApi.delete(id),
    onSuccess: () => {
      invalidate();
      navigate("/leads");
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Delete failed", tone: "error", message: e.message });
    },
  });

  const convert = useMutation({
    mutationFn: () => leadsApi.convert(id),
    onSuccess: async (result) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: brokerKeys.all });
      await dialogs.alert({
        title: "Lead converted",
        tone: "success",
        message: result.message,
      });
      if (result.brokerId) navigate(`/brokers/${result.brokerId}`);
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Conversion failed", tone: "error", message: e.message });
    },
  });

  // Series 6 — send the prospect a pre-filled application link. Creates the
  // broker record at Prospect status, carries over the lead's data + history
  // and runs territory routing (UAT FixBatch-03 FIND-15), instantiates the
  // selected collection bundle(s) into the application package (FIND-18), and
  // hands the lead off to the new broker.
  const inviteToApply = useMutation({
    mutationFn: (vars: {
      collectionBundleIds: string[];
      sendPortalAccessOnly: boolean;
      target?: LeadConversionTarget;
      brokerageId?: string | null;
    }) => leadsApi.inviteToApply(id, vars),
    onSuccess: async (result) => {
      invalidate();
      await dialogs.alert({
        title: "Application invite sent",
        tone: "success",
        message: result.message,
      });
      // Lead Conversion Router — the Brokerage target created a firm (no broker);
      // deep-link to the brokerage. Otherwise deep-link to the created broker.
      if (result.brokerageId) navigate(`/brokerages/${result.brokerageId}`);
      else if (result.brokerId) navigate(`/brokers/${result.brokerId}`);
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Could not send invite", tone: "error", message: e.message });
    },
  });

  const saveNotes = useMutation({
    mutationFn: async () => {
      if (!lead) return;
      await leadsApi.update(id, {
        firstName: lead.firstName,
        lastName: lead.lastName,
        companyName: lead.companyName,
        title: lead.title,
        phone: lead.phone,
        email: lead.email,
        address: lead.address,
        city: lead.city,
        state: lead.state,
        zip: lead.zip,
        source: lead.source,
        sourceDetail: lead.sourceDetail,
        interestLevel: lead.interestLevel,
        notes,
        nextFollowUpDate: lead.nextFollowUpDate,
      });
    },
    onSuccess: () => { invalidate(); setNotesDirty(false); },
  });

  // Tab badges (Layout Standard §5). Each query is enabled only when an
  // id is present; the badges render hidden when N=0.
  const openTaskCountQuery = useOpenTaskCountByRelated("Lead", id || undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("lead", id, 30);
  const notesRecent = useNotesRecentCount("lead", id, 7);
  const activityRecent = useActivityRecentCount("lead", id, 7);

  if (detail.isLoading) return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading…</div>;
  if (!lead) return <div>Lead not found.</div>;

  const transitions = STATUS_TRANSITIONS[lead.status];
  const effectiveNotes = notesDirty ? notes : (lead.notes ?? "");
  // Once a lead is handed off it is locked for everyone but admins, who keep
  // edit access for corrections. The save would be rejected server-side
  // anyway; making the field read-only avoids the misleading Save affordance.
  const notesReadOnly = lead.status === "HandedOff" && !hasRoleCode("ADMIN");
  const displayName = `${lead.firstName} ${lead.lastName}`.trim();

  const tabBadge = (key: TabKey): number | null => {
    switch (key) {
      case "tasks":          return openTaskCount > 0 ? openTaskCount : null;
      case "communications": return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":          return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":       return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:               return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Lead"
        title={displayName}
        description={lead.companyName ?? undefined}
        actions={
          <div className="flex items-center gap-space-2 flex-wrap">
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/leads/${id}/edit`)}>
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
            )}
            {canConvert && lead.status === "Interested" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setInviteOpen(true)}
                disabled={inviteToApply.isPending || !lead.email}
                title={!lead.email ? "Lead has no email" : undefined}
              >
                <Send size={14} strokeWidth={1.5} aria-hidden />
                {inviteToApply.isPending ? "Sending…" : "Send application link"}
              </Button>
            )}
            {canConvert && lead.status === "Interested" && (
              <Button size="sm" onClick={async () => {
                // Lead Activity System §8 + Ownership/Assignment §10 —
                // fetch the carry-over preview and territory projection,
                // then confirm before triggering the conversion.
                let summary = "All call logs, tasks, notes, and activity events" +
                  " will be transferred to the new broker record.";
                try {
                  const preview = await leadsApi.conversionPreview(id);
                  const lines: string[] = [];

                  const parts: string[] = [];
                  if (preview.callLogCount > 0) parts.push(`${preview.callLogCount} call log${preview.callLogCount === 1 ? "" : "s"}`);
                  if (preview.taskCount > 0) parts.push(`${preview.taskCount} task${preview.taskCount === 1 ? "" : "s"}`);
                  if (preview.noteCount > 0) parts.push(`${preview.noteCount} note${preview.noteCount === 1 ? "" : "s"}`);
                  if (preview.activityEventCount > 0) parts.push(`${preview.activityEventCount} activity event${preview.activityEventCount === 1 ? "" : "s"}`);
                  lines.push(parts.length > 0
                    ? `${parts.join(", ")} will be transferred to the new broker record.`
                    : "No prior history — a fresh broker record will be created.");

                  if (preview.state && preview.territoryName) {
                    lines.push(`This lead is in ${preview.state} → ${preview.territoryName}.`);
                  }
                  if (preview.projectedLiaisonUserId) {
                    lines.push(`Will be assigned to: ${preview.projectedLiaisonUserId}${preview.projectedLiaisonIsPrimary ? " (primary liaison)" : ""}.`);
                  }
                  if (preview.projectedAssignmentWarning) {
                    lines.push(`⚠ ${preview.projectedAssignmentWarning}`);
                  }
                  summary = lines.join("\n\n");
                } catch { /* preview is best-effort; fall back to generic copy */ }

                const ok = await dialogs.confirm({
                  title: "Convert to broker?",
                  message: summary,
                  confirmLabel: "Convert",
                });
                if (ok) convert.mutate();
              }} disabled={convert.isPending}>
                <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
                {convert.isPending ? "Converting…" : "Convert to prospect"}
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="tertiary"
                onClick={async () => {
                  const ok = await dialogs.confirm({
                    title: "Delete lead?",
                    message: `Delete ${displayName}? It will be removed from the Leads ` +
                      "list and status counts. This can't be undone from the app.",
                    confirmLabel: "Delete",
                    destructive: true,
                  });
                  if (ok) deleteLead.mutate();
                }}
                disabled={deleteLead.isPending}
              >
                <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                {deleteLead.isPending ? "Deleting…" : "Delete"}
              </Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-space-3 flex-wrap -mt-space-3 mb-space-4">
        <Badge status="gold">{lead.status}</Badge>
        <Badge status="info">{lead.interestLevel}</Badge>
        <Badge>{lead.source}</Badge>
        <span className="text-caption text-gray-mid">
          Created {date(lead.createdAt)}
          {lead.modifiedAt ? ` · Modified ${date(lead.modifiedAt)}` : ""}
        </span>
      </div>

      {/* Layout Standard §6.1 — sticky tab bar below the entity header. */}
      <EntityTabBar
        ariaLabel="Lead sections"
        testIdPrefix="lead"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: tabBadge(it.key) }))}
        value={tab}
        onChange={setTab}
        className="mb-space-4"
      />

      {tab === "overview" && (
        // Container-driven — Table & Tile Standard §12. `max-w-5xl` STAYS: this
        // page self-capped long before the shell's cap moved, and it is the
        // precedent the three pages capped in WS1 follow.
        //
        // FixBatch-06 FIND-33 — `min-w-0` lets these tracks shrink below their
        // content's min-content size, so long unbroken activity-log strings
        // (e.g. broker UUIDs) no longer blow the right column past the viewport
        // edge. `ResponsiveSplit` sets `min-w-0` on its root and on both
        // columns, so that fix survives the conversion by construction rather
        // than by being remembered.
        <ResponsiveSplit
          className="max-w-5xl"
          gap="md"
          mainMinWidth="34rem"
          railWidth={RECORD_RAIL_WIDTH}
          railGrows={false}
          main={
          <div className="grid grid-cols-1 gap-space-4 min-w-0">
            {lead.status === "HandedOff" && lead.convertedToBrokerId && (
              <div className="border-l-[3px] border-status-success bg-status-success/10 p-space-3 rounded-md">
                <div className="flex items-center gap-space-2 font-display font-semibold text-caption uppercase tracking-wide text-status-success">
                  <UserCheck2 size={14} strokeWidth={1.5} aria-hidden /> Converted
                </div>
                <div className="text-small mt-space-1">
                  Converted on {lead.convertedDate ? date(lead.convertedDate) : "—"}.{" "}
                  <Link to={`/brokers/${lead.convertedToBrokerId}`} className="underline text-gold-shadow">
                    View broker →
                  </Link>
                </div>
              </div>
            )}

            <section>
              <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 border border-border-light rounded-md p-space-3 bg-white-pure">
                <Field icon={Phone} label="Phone" value={lead.phone} />
                <Field icon={Mail} label="Email" value={lead.email} />
                <Field icon={Pin} label="Address" value={[lead.address, lead.city, lead.state, lead.zip].filter(Boolean).join(", ")} />
                <Field label="Title" value={lead.title} />
                <Field label="Source detail" value={lead.sourceDetail} />
              </div>
            </section>

            {/* Ownership/Assignment §4 — assigned marketer (read-only display
                for everyone, reassign dropdown for managers/admins). */}
            <section>
              <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Ownership</h3>
              <div className="border border-border-light rounded-md p-space-3 bg-white-pure">
                <AssigneePicker
                  currentUserId={lead.assignedMarketerUserId}
                  rolesFilter="C5,C0,Admin"
                  canEdit={canUpdate}
                  label="Assigned marketer"
                  busy={assign.isPending}
                  onAssign={async (userId) => { await assign.mutateAsync(userId); }}
                />
                {lead.assignedAt && (
                  <div className="mt-space-1 text-caption uppercase tracking-wide text-gray-mid">
                    Assigned {date(lead.assignedAt)}
                    {lead.assignedByUserId ? ` by ${resolveUserName(lead.assignedByUserId)}` : ""}
                  </div>
                )}
                {/* Lead Generation — attribute this lead to the generator who
                    reached out to the caller. Linking marks their prelead
                    Escalated; the $2.50 credit fires when the lead is Contacted. */}
                <div className="mt-space-3 pt-space-3 border-t border-border-light">
                  <LeadGeneratorPicker
                    value={lead.sourceGeneratorId ?? null}
                    valueLabel={lead.sourceGeneratorName ?? null}
                    disabled={!canUpdate}
                    busy={setLeadGenerator.isPending}
                    onChange={(opt) => setLeadGenerator.mutate(opt?.id ?? null)}
                  />
                  {/* Lead Generation — when this lead was escalated from a prelead,
                      open the source prelead in-place to see everything the
                      generator did (raw paste, AI extract, captured links, status). */}
                  {lead.sourcePreleadId && (
                    <button
                      type="button"
                      onClick={() => setSourcePreleadOpen(true)}
                      className="mt-space-2 inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:underline"
                    >
                      <ArrowUpRight size={14} strokeWidth={1.5} aria-hidden /> View source prelead
                    </button>
                  )}
                </div>
              </div>
            </section>

            {canUpdate && transitions.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Status</h3>
                <div className="flex flex-wrap gap-space-2">
                  {/* An archived lead is "put away": offer only Unarchive, not
                      transitions, so it can't be quietly moved while hidden. */}
                  {lead.isArchived ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => unarchive.mutate()}
                      disabled={unarchive.isPending}
                    >
                      <ArchiveRestore size={14} strokeWidth={1.5} aria-hidden />
                      {unarchive.isPending ? "Restoring…" : "Unarchive"}
                    </Button>
                  ) : (
                    <>
                      {transitions.map(t => (
                        <Button
                          key={t}
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            if (t === "Disqualified") {
                              setDisqualifyOpen(true);
                              return;
                            }
                            changeStatus.mutate({ target: t });
                          }}
                          disabled={changeStatus.isPending}
                        >
                          → {t}
                        </Button>
                      ))}
                      {/* Archive is reserved for disqualified leads. */}
                      {lead.status === "Disqualified" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => archive.mutate()}
                          disabled={archive.isPending}
                        >
                          <Archive size={14} strokeWidth={1.5} aria-hidden />
                          {archive.isPending ? "Archiving…" : "Archive"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {lead.isArchived && (
                  <p className="mt-space-2 text-caption text-gray-mid">
                    Archived{lead.archivedAtUtc ? ` ${date(lead.archivedAtUtc)}` : ""} — hidden
                    from the default Leads list and status counts.
                  </p>
                )}
              </section>
            )}

            {/* Lead's own free-text `notes` column on the entity. The
                cross-cutting Notes module (multi-author thread, 24h edit
                window, Compliance moderation) lives on the Notes tab. */}
            <section>
              <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Lead notes</h3>
              <Textarea
                rows={4}
                value={effectiveNotes}
                disabled={notesReadOnly}
                onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
              />
              {notesDirty && !notesReadOnly && (
                <div className="flex justify-end mt-space-2">
                  <Button size="sm" onClick={() => saveNotes.mutate()} disabled={saveNotes.isPending}>
                    {saveNotes.isPending ? "Saving…" : "Save notes"}
                  </Button>
                </div>
              )}
            </section>
          </div>

          }
          /* Layout Standard §4.1 — right rail Recent Activity preview.
             Per Internal Messaging Integration Standard v1.1 §7.3, the
             Related Conversations panel belongs on the Messages tab — not
             on the Overview right rail. */
          rail={
            <aside className="grid grid-cols-1 gap-space-4 min-w-0">
              <LeadOverviewActivityPreview leadId={id} />
            </aside>
          }
        />
      )}

      {/* Brokerage Module v1.0 — covering-firm affiliations for this lead. */}
      {tab === "brokerages" && (
        <BrokerageAffiliationsPanel partnerId={id} partnerKind="Lead" />
      )}

      {tab === "documents" && (
        <DocumentPanel ownerType="Lead" ownerId={id} rootName={displayName} />
      )}

      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="Lead"
          relatedEntityId={id}
          addButtonLabel="+ Add task linked to this Lead"
        />
      )}

      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="Lead"
          relatedEntityId={id}
          entityLabel={displayName}
          entityArchived={lead.isArchived}
        />
      )}

      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="lead"
          resourceId={id}
          entityLabel={displayName}
        />
      )}

      {tab === "communications" && (
        <CommunicationsTabPanel
          ownerType="lead"
          ownerId={id}
          ownerLabel={displayName}
        />
      )}

      {tab === "notes" && (
        <NotesTabPanel ownerType="lead" ownerId={id} />
      )}

      {tab === "activity" && (
        <ActivityTabPanel ownerType="lead" ownerId={id} />
      )}

      <SendApplicationLinkModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        leadEmail={lead.email ?? null}
        canManageBundles={hasPermission("Collection.ManageTemplates")}
        showTypeRouter
        onConfirm={(vars) => {
          setInviteOpen(false);
          inviteToApply.mutate(vars);
        }}
      />

      <DisqualifyLeadModal
        open={disqualifyOpen}
        onClose={() => setDisqualifyOpen(false)}
        pending={changeStatus.isPending}
        onConfirm={(reason, notesText) =>
          changeStatus.mutate({
            target: "Disqualified",
            reason,
            notes: notesText || undefined,
          })
        }
      />

      {/* Area A — the source prelead opened in-place over the Lead, read-only and
          fed by the Leads.Read source-prelead endpoint (no staff actions here). */}
      {sourcePreleadOpen && lead.sourcePreleadId && (
        <PreleadDetailDrawer
          preleadId={lead.sourcePreleadId}
          sourceOfLeadId={lead.id}
          showStaffActions={false}
          onClose={() => setSourcePreleadOpen(false)}
        />
      )}
    </>
  );
}

function Field({
  label,
  value,
  icon: Icon,
}: { label: string; value?: string | null; icon?: React.ComponentType<{ size: number; strokeWidth: number; "aria-hidden": true }> }) {
  return (
    <div>
      <div className="flex items-center gap-space-1 text-caption uppercase tracking-wide text-gray-mid mb-space-1">
        {Icon && <Icon size={12} strokeWidth={1.5} aria-hidden />}
        {label}
      </div>
      <div className="text-small text-onyx">{value || "—"}</div>
    </div>
  );
}
